import React, { useState } from 'react';
import {
    TextInput,
    ActionIcon,
    Drawer,
    Stack,
    Paper,
    Text,
    Group,
    ScrollArea,
    Button
} from '@mantine/core';
import { IconSelector, IconCheck } from '@tabler/icons-react';

// Regex patterns list
const REGEX_PATTERNS = [
    {
        title: 'Alphanumeric Text',
        regex: "^[a-zA-Z0-9\u0080-\uFFFF _'-]{1,100}$",
        description: 'Alphanumeric characters and spaces (1-100 characters)',
    },
    {
        title: 'Email Validation',
        regex: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
        description: 'Valid email format (e.g., abc@example.com, abc@example.com.kw)',
    },
    {
        title: 'Kuwait Mobile Number',
        regex: '^[569][0-9]{7}$',
        description: 'Kuwait mobile number starting with 5, 6, or 9 (8 digits)',
    },
    {
        title: 'International Mobile Number',
        regex: '^[0-9]{6,15}$',  // Most countries: 6-15 digits
        description: 'Mobile number with 6-15 digits (international format)',
    },
    {
        title: 'PACI Civil ID',
        regex: 'CivilID',
        description: 'Kuwait Civil ID number (12 digits)',
    },
    {
        title: 'Kuwait IBAN',
        regex: 'IBAN',
        description: 'Kuwait IBAN format',
    },
    {
        title: 'Username',
        regex: '^[a-zA-Z0-9_]{3,20}$',
        description: 'Username with letters, numbers, and underscore (3-20 characters)',
    },
    {
        title: 'URL/Website',
        regex: '^(https?:\\/\\/)?(www\\.)?[a-zA-Z0-9-]+(\\.[a-zA-Z]{2,})+(\\/.*)?$',
        description: 'Valid website URL',
    },
    {
        title: 'Postal Code',
        regex: '^[0-9]{5}$',
        description: 'Postal code with 5 digits',
    },
    {
        title: 'Positive Number',
        regex: '^[1-9][0-9]*$',
        description: 'Positive whole number (no zero)',
    },
    {
        title: 'Decimal Number',
        regex: '^[0-9]+(\\.[0-9]{1,2})?$',
        description: 'Number with up to 2 decimal places',
    },
    {
        title: 'Passport Number',
        regex: '^[A-Z0-9]{6,9}$',
        description: 'Passport number (6-9 alphanumeric characters)',
    }
];

interface SimpleRegexSelectorProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
}

/**
 * Simple Regex Selector - Read-only with side drawer
 * 
 * @example
 * <SimpleRegexSelector
 *   value={field.regex || ''}
 *   onChange={(value) => onUpdate('regex', value)}
 *   required
 * />
 */
const SimpleRegexSelector: React.FC<SimpleRegexSelectorProps> = ({
    value,
    onChange,
    label = 'Regex Validation',
    placeholder = 'Click to select regex pattern',
    required = false
}) => {
    const [opened, setOpened] = useState(false);
    const [customRegex, setCustomRegex] = useState('');

    const handleSelect = (regex: string) => {
        onChange(regex);
        setOpened(false);
        setCustomRegex('');
    };

    const handleCustomApply = () => {
        if (customRegex) {
            onChange(customRegex);
            setOpened(false);
            setCustomRegex('');
        }
    };

    // Check if typed regex matches any predefined
    const matchedPattern = REGEX_PATTERNS.find(p => p.regex === customRegex);

    return (
        <>
            <TextInput
                label={label}
                value={value}
                required={required}
                placeholder={placeholder}
                readOnly
                onClick={() => setOpened(true)}
                style={{ cursor: 'pointer' }}
                rightSection={
                    <ActionIcon variant="subtle" onClick={() => setOpened(true)}>
                        <IconSelector size={16} />
                    </ActionIcon>
                }
            />

            <Drawer
                opened={opened}
                onClose={() => {
                    setOpened(false);
                    setCustomRegex('');
                }}
                title="Select Regex Pattern"
                position="right"
                size="lg"
            >
                <Stack gap="md">
                    {/* Single Input Box */}
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Text size="sm" fw={600}>Type or Paste Regex Pattern</Text>
                            <TextInput
                                placeholder="Type regex pattern..."
                                value={customRegex}
                                onChange={(e) => setCustomRegex(e.target.value)}
                                autoFocus
                            />

                            {/* Show if matched */}
                            {matchedPattern && customRegex && (
                                <Paper p="sm" bg="green.0" withBorder>
                                    <Text size="xs" fw={600} c="green.8">✓ Matched: {matchedPattern.title}</Text>
                                    <Text size="xs" c="dimmed">{matchedPattern.description}</Text>
                                </Paper>
                            )}

                            {/* Show if not matched */}
                            {customRegex && !matchedPattern && (
                                <Paper p="sm" bg="blue.0" withBorder>
                                    <Text size="xs" fw={600} c="blue.8">Custom Pattern</Text>
                                    <Text size="xs" c="dimmed">This will be saved as custom regex</Text>
                                </Paper>
                            )}

                            <Button
                                onClick={handleCustomApply}
                                disabled={!customRegex}
                                fullWidth
                            >
                                Apply Pattern
                            </Button>
                        </Stack>
                    </Paper>

                    <Text size="sm" c="dimmed" ta="center">Or select from list</Text>

                    {/* Patterns List */}
                    <ScrollArea style={{ height: 'calc(100vh - 350px)' }}>
                        <Stack gap="xs">{REGEX_PATTERNS.map((pattern, index) => (
                            <Paper
                                key={index}
                                p="sm"
                                withBorder
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: pattern.regex === value ? '#e7f5ff' : undefined,
                                    borderColor: pattern.regex === value ? '#228be6' : undefined
                                }}
                                onClick={() => handleSelect(pattern.regex)}
                            >
                                <Group justify="space-between">
                                    <div>
                                        <Text size="sm" fw={600}>{pattern.title}</Text>
                                        <Text size="xs" c="dimmed">{pattern.description}</Text>
                                        <Text size="xs" c="dimmed" mt={4} style={{ fontFamily: 'monospace' }}>
                                            {pattern.regex}
                                        </Text>
                                    </div>
                                    {pattern.regex === value && (
                                        <IconCheck size={20} color="#228be6" />
                                    )}
                                </Group>
                            </Paper>
                        ))}
                        </Stack>
                    </ScrollArea>
                </Stack>
            </Drawer>
        </>
    );
};

export default SimpleRegexSelector;

/**
 * USAGE - EXACTLY LIKE TEXTINPUT:
 * 
 * Replace this:
 * <TextInput
 *   label="Regex Validation"
 *   value={field.regex || ''}
 *   onChange={(e) => onUpdate('regex', e.target.value)}
 *   required
 * />
 * 
 * With this:
 * <SimpleRegexSelector
 *   value={field.regex || ''}
 *   onChange={(value) => onUpdate('regex', value)}
 *   required
 * />
 */